// Reset your password (spec C7.1): the same answer whether or not the email has an account.
// Markup from the SignInCard and Banner cards and the "Sign in and system states" board.
import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { Api } from '../auth/api';
import { copy, rateLimitText } from '../auth/copy';
import { Brand, CardTitle, ErrorLine, SignInLayout } from '../auth/SignInLayout';
import { CheckIcon } from '../shell/icons';
import type { SignInState } from './SignInPage';

const looksLikeEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export function ForgotPasswordPage({ api }: { api: Api }) {
  const state = useLocation().state as { email?: string } | null;
  const navigate = useNavigate();
  // Keeps the email typed on Sign in.
  const [email, setEmail] = useState(state?.email ?? '');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSend = looksLikeEmail(email.trim()) && !busy;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSend) return;
    setBusy(true);
    setError(null);
    const res = await api.forgot(email.trim());
    setBusy(false);
    if (res.ok) setSent(true);
    else if (res.error === 'rate_limited') setError(rateLimitText(res));
    else setError(copy.somethingWrong);
  }

  const backToSignIn = () =>
    navigate('/sign-in', { state: { email: email.trim() } satisfies SignInState });

  return (
    <SignInLayout footnote={copy.footnote}>
      <form className="lcard" aria-labelledby="forgot-title" noValidate onSubmit={submit}>
        <Brand />
        <CardTitle id="forgot-title" title={copy.resetTitle}>
          {copy.resetIntro}
        </CardTitle>
        {sent ? (
          <div className="bnr green" role="status">
            <CheckIcon />
            <span>{copy.resetSent}</span>
          </div>
        ) : (
          <>
            <div className="lf">
              <label htmlFor="forgot-email">Email</label>
              <input
                id="forgot-email"
                className="input"
                type="email"
                autoComplete="username"
                placeholder="you@safeside.example"
                autoFocus
                maxLength={254}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {error && <ErrorLine text={error} />}
            <button type="submit" className="btn btn-primary wide" disabled={!canSend}>
              {busy ? copy.sendingResetLink : copy.sendResetLink}
            </button>
          </>
        )}
        <div className="row" style={{ justifyContent: 'center' }}>
          <button type="button" className="linkbtn" onClick={backToSignIn}>
            {copy.backToSignIn}
          </button>
        </div>
      </form>
    </SignInLayout>
  );
}
